import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#F2EEE7]">
      {/* Navigation */}
      <nav className="border-b border-[#D4CFC7] bg-[#F2EEE7]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C4033] hover:text-[#7A1E2C] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-serif font-bold text-[#5C4033] mb-4">Privacy Policy</h1>
        <p className="text-[#6B5B4F] mb-12">Last updated: 25 March 2026</p>

        <div className="prose prose-lg max-w-none text-[#5C4033]">
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal ("Sharedequal", "we", "us", or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard personal data when you access or use our website and services (collectively, the "Services").
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            By using the Services, you acknowledge that you have read and understood this Privacy Policy.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">1. Personal Data We Collect</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            We may collect personal data that you provide directly to us, including but not limited to:
          </p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>Name</li>
            <li>Email address</li>
            <li>Telephone number or other contact details</li>
            <li>Information submitted through forms, including drop requests, shared order details, item specifications, and sizing information</li>
            <li>Any information you voluntarily provide through communications with us (including email, messaging platforms, or social media)</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            We do not intentionally collect sensitive personal data unless required for the provision of our Services and expressly provided by you.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">2. Purposes of Collection and Use</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            We collect and use personal data for the following purposes:
          </p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>To organize, manage, and administer shared shipment requests and participation in drops</li>
            <li>To contact you regarding drops you have requested or joined</li>
            <li>To provide updates relating to shipments, payments, collection arrangements, or operational matters</li>
            <li>To respond to enquiries, feedback, or support requests</li>
            <li>To improve and optimise our website, services, and user experience</li>
            <li>To comply with legal, regulatory, or operational requirements</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Personal data is used solely for purposes reasonably related to the provision of Sharedequal's Services.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">3. Disclosure of Personal Data</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Sharedequal does not sell, rent, or trade personal data.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            We may disclose personal data on a limited and need-to-know basis in the following circumstances:
          </p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>To logistics partners or service providers solely for the purpose of coordinating shipments or collections</li>
            <li>Where disclosure is required or permitted by law, regulation, court order, or governmental authority</li>
            <li>Where necessary to protect Sharedequal's legal rights or to prevent fraud or misuse of the Services</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            All third parties receiving personal data are required to handle such data in accordance with applicable data protection laws.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">4. Data Storage and Security</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Sharedequal takes reasonable administrative, technical, and physical measures to protect personal data from unauthorised access, disclosure, alteration, or destruction.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            While we endeavour to protect personal data, no method of transmission over the internet or method of electronic storage is completely secure. Accordingly, Sharedequal does not warrant or guarantee the absolute security of personal data.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">5. Retention of Personal Data</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Personal data is retained only for as long as necessary to fulfil the purposes for which it was collected, or as required by applicable laws, regulations, or operational needs. When personal data is no longer required, it will be securely deleted or anonymised.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">6. Cookies and Analytics</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Sharedequal may use cookies and analytics tools to collect information about how users interact with the website, including pages visited and usage patterns. Such information is used to improve functionality and performance and does not directly identify individual users.
          </p>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            You may adjust your browser settings to refuse cookies or to notify you when cookies are being used. Please note that disabling cookies may affect certain features of the website.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">7. Your Rights</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-4">
            Subject to applicable laws, you may have the right to:
          </p>
          <ul className="list-disc pl-6 text-[#6B5B4F] space-y-2 mb-4">
            <li>Request access to your personal data</li>
            <li>Request correction of inaccurate or incomplete personal data</li>
            <li>Request deletion of personal data, subject to legal or operational obligations</li>
          </ul>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Requests may be made by contacting us using the details provided below. We may require verification of identity before processing any request.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">8. Third-Party Websites</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            The Sharedequal website may contain links to third-party websites. Sharedequal is not responsible for the content, security, or privacy practices of such third-party sites. Access to third-party websites is at your own risk.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">9. Changes to This Privacy Policy</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            Sharedequal may update this Privacy Policy from time to time. Any changes will be published on this page with an updated revision date. Continued use of the Services after such updates constitutes acceptance of the revised Privacy Policy.
          </p>

          <h2 className="text-2xl font-serif font-bold text-[#5C4033] mt-12 mb-4">10. Contact Information</h2>
          <p className="text-[#6B5B4F] leading-relaxed mb-8">
            If you have any questions, requests, or concerns regarding this Privacy Policy or the handling of personal data, please contact us at{' '}
            <a href="mailto:sharedequal@gmail.com" className="text-[#7A1E2C] hover:underline">sharedequal@gmail.com</a>
            {' '}or via our{' '}
            <Link to="/contact" className="text-[#7A1E2C] hover:underline">Contact page</Link>.
          </p>
        </div>

        <div className="mt-12 pt-8 border-t border-[#D4CFC7]">
          <Link to="/" className="inline-flex items-center gap-2 text-[#7A1E2C] hover:underline font-medium">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
